// Mock data for RAMBA NEWS. Replace with API/CMS calls later.
// All public-facing fields are operationally safe (no locations, no names).
import type { MediaItem } from "@/types/article.types";
import type { VerificationAttachmentDto } from "@/types/verification.types";

export type Verdict = "false" | "misleading" | "unproven" | "true" | "context";
export type Priority = "confirmed" | "developing" | "correction" | "alert";
export type ContentType =
  | "article"
  | "alert"
  | "factcheck"
  | "explainer"
  | "video"
  | "audio"
  | "live"
  | "editorial"
  | "correction"
  | "syndicated";
  
export type LangCode = "fr" | "en" | "bm" | "ha" | "mos";

export interface Multilingual {
  fr?: string;
  en?: string;
  bm?: string;
  ha?: string;
  mos?: string;
}

// ─── Types publics (shape attendu par les composants) ────────────────────────

// mock-data.ts — dans l'interface Article, ajouter après isCorrected
export interface Article {
  id: string;
  slug: string;
  title: Multilingual;
  subtitle?: Multilingual;
  summary?: Multilingual;
  categories: string[];
  pays: string[];
  tags: string[];
  type: ContentType;
  publishedAt: string;
  updatedAt?: string;
  readingTime: number;
  availableLanguages: LangCode[];
  hasAudio?: boolean;
  hasAvatarVideo?: boolean;
  isCorrected?: boolean;
  byline: "desk" | "partner-desk";
  // ✅ Médias réels
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  coverImageUrl?: string;
  medias?: MediaItem[];
}

export interface FactCheck {
  id: string;
  slug: string;
  claim: Multilingual;
  verdict: Verdict;
  shortExplanation: Multilingual;
  whatIsCirculating?: Multilingual;
  whatWeVerified?: Multilingual;
  evidence?: Multilingual;
  whyItMatters?: Multilingual;
  safeShare?: Multilingual;
  date: string;
  category: string;
  format: "text" | "image" | "audio" | "video" | "screenshot";
  attachments?: VerificationAttachmentDto[];
}

export interface LiveUpdate {
  id: string;
  liveId: string;
  timestamp: string;
  priority: Priority;
  text: Multilingual;
  pinned?: boolean;
}

export interface LiveEvent {
  id: string;
  title: Multilingual;
  startedAt: string;
  closed?: boolean;
  updates: LiveUpdate[];
}

export const CATEGORIES = [
  { slug: "sahel", fr: "Sahel", en: "Sahel" },
  { slug: "governance", fr: "Gouvernance", en: "Governance" },
  { slug: "security", fr: "Sécurité", en: "Security" },
  { slug: "society", fr: "Société", en: "Society" },
  { slug: "economy", fr: "Économie", en: "Economy" },
  { slug: "media-literacy", fr: "Éducation aux médias", en: "Media Literacy" },
  { slug: "factcheck", fr: "Vérification", en: "Fact-check" },
  { slug: "explainers", fr: "Décryptages", en: "Explainers" },
  { slug: "live", fr: "Direct", en: "Live" },
] as const;

export const LANGUAGES: { code: LangCode; fr: string; en: string }[] = [
  { code: "fr", fr: "Français", en: "French" },
  { code: "en", fr: "Anglais", en: "English" },
  // { code: "bm", fr: "Bambara", en: "Bambara" },
  // { code: "ha", fr: "Haoussa", en: "Hausa" },
  // { code: "mos", fr: "Mooré", en: "Mooré" },
];

const now = Date.now();
const hoursAgo = (h: number) => new Date(now - h * 3600_000).toISOString();
const daysAgo = (d: number) => new Date(now - d * 86400_000).toISOString();

export const ARTICLES: Article[] = [
  
];

export const FACT_CHECKS: FactCheck[] = [
  
];

export const LIVE_EVENTS: LiveEvent[] = [
  {
    id: "l1",
    title: {
      fr: "Direct : suivi institutionnel — mises à jour vérifiées",
      en: "Live: institutional tracking — verified updates",
    },
    startedAt: hoursAgo(6),
    updates: [
      {
        id: "u1",
        liveId: "l1",
        timestamp: hoursAgo(0.2),
        priority: "confirmed",
        text: {
          fr: "Mise à jour confirmée par recoupement avec sources officielles.",
          en: "Update confirmed by cross-checking with official sources.",
        },
        pinned: true,
      },
      {
        id: "u2",
        liveId: "l1",
        timestamp: hoursAgo(1),
        priority: "developing",
        text: {
          fr: "Information en cours de vérification. Les détails restent à confirmer.",
          en: "Information being verified. Details remain to be confirmed.",
        },
      },
      {
        id: "u3",
        liveId: "l1",
        timestamp: hoursAgo(2),
        priority: "correction",
        text: {
          fr: "Correction : un chiffre précédemment publié a été ajusté après nouvelle vérification.",
          en: "Correction: a previously published figure has been adjusted after re-verification.",
        },
      },
      {
        id: "u4",
        liveId: "l1",
        timestamp: hoursAgo(3),
        priority: "alert",
        text: {
          fr: "Alerte vérifiée : situation à suivre avec prudence. Pas de spéculation.",
          en: "Verified alert: situation to monitor cautiously. No speculation.",
        },
      },
      {
        id: "u5",
        liveId: "l1",
        timestamp: hoursAgo(5),
        priority: "confirmed",
        text: {
          fr: "Ouverture du suivi en direct. Mises à jour publiées au fil de la vérification.",
          en: "Live tracking opened. Updates published as verification progresses.",
        },
      },
    ],
  },
];

export const TRENDING_NARRATIVES = [
  { id: "n1", claim: "Image recontextualisée en circulation", platforms: ["WhatsApp", "X"], risk: "high", status: "verifying" },
  { id: "n2", claim: "Audio coupé attribué à un responsable", platforms: ["Telegram"], risk: "medium", status: "monitoring" },
  { id: "n3", claim: "Statistique sans source méthodologique", platforms: ["Facebook"], risk: "low", status: "debunked" },
  { id: "n4", claim: "Document d'apparence officielle non authentifié", platforms: ["X", "Telegram"], risk: "high", status: "verifying" },
];

export const ANALYTICS_TOP_ARTICLES = ARTICLES.slice(0, 5).map((a, i) => ({
  ...a,
  views: 12000 - i * 1800,
  shares: 850 - i * 130,
}));
